import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ShoppingCart, Heart, Minus, Plus, Package, Truck, ArrowLeft, ChevronLeft, ChevronRight, Tag, Info, Layers } from 'lucide-react'
import { productService, promotionService } from '../services/api'
import { useCartStore } from '../store/cartStore'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useWishlistStore } from '../store/wishlistStore'
import { PageSpinner, ErrorMessage, Breadcrumbs, Badge } from '../components/ui'
import ProductCard from '../components/product/ProductCard'

function formatPrice(p) {
  if (!p && p !== 0) return 'POA'
  return `R ${parseFloat(p).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

function getCategoryEmoji(product) {
  const cat      = (product?.category_name || '').toLowerCase()
  const desc     = (product?.description || product?.name || '').toLowerCase()
  const brand    = (product?.brand || '').toLowerCase()
  const combined = `${desc} ${brand}`

  // Category name — most reliable, check first
  if (/whiskey|whisky|bourbon|scotch/.test(cat))     return '🥃'
  if (/vodka/.test(cat))                              return '🍸'
  if (/\bgin\b/.test(cat))                            return '🌿'
  if (/tequila/.test(cat))                            return '🌵'
  if (/rum/.test(cat))                                return '🏝️'
  if (/brandy|cognac/.test(cat))                      return '🍂'
  if (/liqueur/.test(cat))                            return '✨'
  if (/aperitif/.test(cat))                           return '🫗'
  if (/\bcane\b/.test(cat))                           return '🌾'
  if (/shooter/.test(cat))                            return '🔫'
  if (/champagne|sparkling/.test(cat))                return '🥂'
  if (/port|sherry|muscadel/.test(cat))               return '🫙'
  if (/box wine/.test(cat))                           return '📦'
  if (/wine/.test(cat))                               return '🍷'
  if (/craft beer/.test(cat))                         return '🍻'
  if (/cider/.test(cat))                              return '🍋'
  if (/rtd cocktail/.test(cat))                       return '🍹'
  if (/rtd/.test(cat))                                return '🥤'
  if (/beer/.test(cat))                               return '🍺'
  if (/energy/.test(cat))                             return '⚡'
  if (/juice/.test(cat))                              return '🍊'
  if (/cordial/.test(cat))                            return '🍓'
  if (/water/.test(cat))                              return '🫧'
  if (/mineral/.test(cat))                            return '💧'
  if (/cigarette|tobacco/.test(cat))                  return '🚬'
  if (/spirits/.test(cat))                            return '🥃'
  if (/snack|chips|crisp|nut|biltong|droëwors|popcorn/.test(cat)) return '🍿'
  if (/glassware|glass/.test(cat))                    return '🥂'
  if (/gift|hamper|accessor/.test(cat))               return '🎁'

  // SA snack brands checked before alcohol fallback
  if (/willards|simba|lays|pringles|doritos|niknaks|nik nak|cheesenak|cheese nak|biltong|droëwors|droewors|popcorn|pretzels|peanut|nachos|chippies/.test(combined)) return '🍿'

  // Alcohol brands
  if (/\bjw\b|johnnie walker|glenfiddich|jameson|jack daniel|highland|macallan|chivas|ballantine/.test(combined)) return '🥃'
  if (/\bsmirnoff\b|absolut|belvedere|grey goose|ciroc/.test(combined)) return '🍸'
  if (/\bbeefeater\b|hendrick|tanqueray|bombay|inverroche/.test(combined)) return '🌿'
  if (/\bcaptain morgan\b|bacardi|havana|malibu/.test(combined))         return '🏝️'
  if (/\bklipdrift\b|richelieu|old brown|van ryn|hennessy|brandy/.test(combined)) return '🍂'
  if (/\bamarula\b|baileys|kahlua|disaronno|frangelico/.test(combined))  return '✨'
  if (/\bcastle\b|\bbud\b|\bheineken\b|\bcarlsberg\b|\bwindhoek\b/.test(combined)) return '🍺'

  // Description keyword fallback
  if (/whiskey|whisky|bourbon|scotch/.test(combined)) return '🥃'
  if (/vodka/.test(combined))                          return '🍸'
  if (/\bgin\b/.test(combined))                        return '🌿'
  if (/tequila|mezcal/.test(combined))                 return '🌵'
  if (/\brum\b/.test(combined))                        return '🏝️'
  if (/brandy|cognac/.test(combined))                  return '🍂'
  if (/liqueur/.test(combined))                        return '✨'
  if (/champagne|sparkling|prosecco|mcc/.test(combined)) return '🥂'
  if (/port|sherry|muscadel/.test(combined))           return '🫙'
  if (/wine/.test(combined))                           return '🍷'
  if (/craft beer|craft/.test(combined))               return '🍻'
  if (/cider/.test(combined))                          return '🍋'
  if (/rtd cocktail/.test(combined))                   return '🍹'
  if (/rtd/.test(combined))                            return '🥤'
  if (/beer|lager|ale|stout/.test(combined))           return '🍺'
  if (/energy drink|monster|red bull/.test(combined))  return '⚡'
  if (/juice|nectar|fruit/.test(combined))             return '🍊'
  if (/cordial|squash|syrup/.test(combined))           return '🍓'
  if (/water/.test(combined))                          return '🫧'
  if (/mineral|soda|soft drink/.test(combined))        return '💧'
  if (/cigarette|tobacco/.test(combined))              return '🚬'
  if (/glass|glassware/.test(combined))                return '🥂'
  if (/gift|hamper/.test(combined))                    return '🎁'
  if (/snack|nut|chip|crisp|popcorn|biltong/.test(combined)) return '🍿'

  return '🍾'
}

function formatUom(product) {
  const ps = product?.packsize
  const desc = (product?.description || '').toLowerCase()
  const cat  = (product?.category_name || '').toLowerCase()
  if (!ps) return null
  if (/keg/.test(desc + cat))              return { label: 'Keg', emoji: '🪣' }
  if (/can|cans/.test(desc + cat) && ps)   return { label: `${ps}-Pack Cans`, emoji: '🥫' }
  if (ps === 1)                            return { label: 'Single Unit', emoji: '1️⃣' }
  if (ps === 6)                            return { label: '6-Pack', emoji: '📦' }
  if (ps === 12)                           return { label: '12-Pack', emoji: '📦' }
  if (ps === 24)                           return { label: 'Case of 24', emoji: '🗃️' }
  if (ps === 48)                           return { label: 'Case of 48', emoji: '🗃️' }
  if (ps >= 20)                            return { label: `Case of ${ps}`, emoji: '🗃️' }
  return { label: `Pack of ${ps}`, emoji: '📦' }
}

function PromotionsTab({ productId }) {
  const { data: promos = [] } = useQuery({
    queryKey: ['promotions-public'],
    queryFn: () => promotionService.getMyPromos().catch(() => []),
    staleTime: 5 * 60 * 1000,
  })
  const list = Array.isArray(promos) ? promos : (promos?.items ?? [])
  // Filter promos that are active and either site-wide or target this product
  const now = new Date()
  const active = list.filter(p => {
    const start = p.starts_at ? new Date(p.starts_at) : null
    const end   = p.ends_at   ? new Date(p.ends_at)   : null
    if (start && start > now) return false
    if (end   && end   < now) return false
    return true
  })

  if (!active.length) return (
    <div className="text-center py-10 text-gray-400">
      <span className="text-4xl block mb-3">🎁</span>
      <p className="text-sm font-display">No active promotions at the moment.</p>
      <p className="text-xs mt-1 text-gray-500">Check back soon for deals!</p>
    </div>
  )

  return (
    <div className="space-y-4">
      {active.map(p => {
        const end = p.ends_at ? new Date(p.ends_at) : null
        const msLeft = end ? end - now : null
        const daysLeft = msLeft ? Math.ceil(msLeft / 86400000) : null
        return (
          <div key={p.id} className="p-4 rounded-xl border border-gold/30 bg-gold/5 dark:bg-gold/10 flex gap-4 items-start">
            <div className="text-3xl flex-shrink-0">🎁</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-display font-semibold text-gray-800 dark:text-white text-sm">{p.title}</h4>
              </div>
              {p.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{p.description}</p>
              )}
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {p.discount_type === 'percentage' && p.discount_value && (
                  <span className="text-xs font-display font-semibold text-green-600 dark:text-green-400">
                    {p.discount_value}% off
                  </span>
                )}
                {p.discount_type === 'fixed' && p.discount_value && (
                  <span className="text-xs font-display font-semibold text-green-600 dark:text-green-400">
                    R{p.discount_value} off
                  </span>
                )}
                {daysLeft !== null && (
                  <span className={`text-xs font-display ${daysLeft <= 3 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                    {daysLeft <= 0 ? 'Ends today!' : `${daysLeft}d left`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function ProductDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const addItem = useCartStore(s => s.addItem)
  const { toggle, isInWishlist } = useWishlistStore()
  const [qty, setQty] = useState(1)
  const [qtyDraft, setQtyDraft] = useState('1')

  // Keep draft in sync when qty changes via +/- buttons
  useEffect(() => { setQtyDraft(String(qty)) }, [qty])
  const [adding, setAdding] = useState(false)
  const [activeTab, setActiveTab] = useState('description')
  const [selectedImage, setSelectedImage] = useState(null)

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productService.getById(id),
  })

  const { data: imagesData } = useQuery({
    queryKey: ['product-images', id],
    queryFn: () => productService.getImages(id),
    enabled: !!product,
    staleTime: 5 * 60 * 1000,
  })

  const { data: related } = useQuery({
    queryKey: ['products', 'related', id],
    queryFn: () => productService.getAll({ limit: 4, exclude: id }),
    enabled: !!product,
  })



  if (isLoading) return <PageSpinner />
  if (error || !product) return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <ErrorMessage message="Product not found." />
      <div className="text-center mt-4">
        <Link to="/shop" className="btn-primary">Back to Shop</Link>
      </div>
    </div>
  )

  const wished = isInWishlist(product.id)

  // Build image list from the images endpoint, fall back to primary_image only
  const allImages = imagesData?.images?.length
    ? imagesData.images
    : product.primary_image
      ? [{ path: product.primary_image, url: `/MappedData/${product.primary_image}` }]
      : []

  const currentImg = selectedImage || allImages[0] || null
  const currentIndex = allImages.findIndex(i => i.url === currentImg?.url)
  const imageUrl = currentImg?.url || null

  const goNext = () => {
    const next = allImages[(currentIndex + 1) % allImages.length]
    setSelectedImage(next)
  }
  const goPrev = () => {
    const prev = allImages[(currentIndex - 1 + allImages.length) % allImages.length]
    setSelectedImage(prev)
  }

  const name = product.description || product.product_name || product.name || 'Product'
  // inclprice = VAT-inclusive selling price. exclprice is ex-VAT only, not a discount.
  const price = product.inclprice || product.inclprice2 || product.price
  const stockOk = product.stock_quantity === undefined || product.stock_quantity > 0
  const uom = formatUom(product)
  const emoji = getCategoryEmoji(product)

  const handleAddToCart = async () => {
    setAdding(true)
    await addItem(product, qty)
    setTimeout(() => setAdding(false), 800)
  }

  const TABS = [
    { key: 'description', label: '📋 Description' },
    { key: 'details',     label: '🔍 Details' },
    { key: 'promotions',  label: '🎁 Promotions' },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 animate-fade-in">

      {/* Breadcrumbs row — no back button here anymore */}
      <div className="mb-4">
        <Breadcrumbs items={[
          { href: '/', label: 'Home' },
          { href: '/shop', label: 'Shop' },
          { label: name },
        ]} />
      </div>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Image Gallery */}
        <div className="space-y-3">
          {/* Main image */}
          <div className="aspect-square rounded-2xl overflow-hidden bg-white flex items-center justify-center relative group">
            {imageUrl ? (
              <>
                <img
                  key={imageUrl}
                  src={imageUrl}
                  alt={name}
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                  className="w-full h-full object-contain p-8 transition-transform duration-300 group-hover:scale-105"
                />
                <div className="hidden w-full h-full flex-col items-center justify-center p-12">
                  <div className="text-8xl mb-4">{getCategoryEmoji(product)}</div>
                  <p className="text-gray-400 font-display text-center">{name}</p>
                </div>
              </>
            ) : (
              <div className="text-center p-12">
                <div className="text-8xl mb-4">{getCategoryEmoji(product)}</div>
                <p className="text-gray-400 font-display">{name}</p>
              </div>
            )}

            {/* Prev/Next arrows — only when multiple images */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-md flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-md flex items-center justify-center text-gray-700 dark:text-gray-200 hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                >
                  <ChevronRight size={16} />
                </button>
                {/* Dot indicator */}
                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                  {allImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(allImages[i])}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${
                        i === currentIndex
                          ? 'bg-primary w-4'
                          : 'bg-gray-300 dark:bg-gray-600 hover:bg-primary/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail strip — only when 2+ images */}
          {allImages.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
              {allImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(img)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 bg-white transition-all ${
                    currentImg?.url === img.url
                      ? 'border-primary ring-2 ring-primary/20 scale-105'
                      : 'border-gray-200 dark:border-gray-600 hover:border-primary/60 opacity-70 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.url}
                    alt={`View ${i + 1}`}
                    onError={e => { e.target.parentNode.style.display = 'none' }}
                    className="w-full h-full object-contain"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-5">
          {/* Back + Brand row — compact, not stretched */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm font-display font-semibold bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-xl shadow-sm transition-all hover:shadow-md active:scale-95 group"
            >
              <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
              Back
            </button>
            {product.brand && (
              <>
                <span className="text-gray-300 dark:text-gray-600 text-xs select-none">·</span>
                <button
                  onClick={() => navigate(`/shop?brand=${encodeURIComponent(product.brand)}`)}
                  className="inline-flex items-center gap-1.5 text-xs font-display font-semibold uppercase tracking-wider text-primary border border-primary/30 bg-primary/10 rounded-full px-3 py-1 hover:bg-primary/20 transition-colors"
                >
                  🏷️ {product.brand}
                </button>
              </>
            )}
          </div>

          <h1 className="font-heading text-2xl md:text-3xl text-gray-900 dark:text-white leading-tight">
            {name}
          </h1>

          {product.short_description && (
            <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
              {product.short_description}
            </p>
          )}

          {/* Info pills row */}
          <div className="flex flex-wrap gap-2 items-center">
            {stockOk ? (
              <Badge variant="green">✅ In Stock</Badge>
            ) : (
              <Badge variant="gray">❌ Out of Stock</Badge>
            )}
            {uom && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-display font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                {uom.emoji} {uom.label}
              </span>
            )}
            {product.category_name && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-display font-semibold bg-primary/10 text-primary dark:bg-primary/20 dark:text-teal-300">
                {emoji} {product.category_name}
              </span>
            )}
            {product.on_promotion && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-display font-semibold bg-gold/15 text-amber-700 dark:text-gold border border-gold/30">
                🎁 On Promotion
              </span>
            )}
          </div>

          {/* Price */}
          <div className="py-4 border-y border-gray-100 dark:border-gray-700">
            <div className="flex items-baseline gap-3">
              <div className="text-3xl font-heading text-primary dark:text-red-400">
                {formatPrice(price)}
              </div>
              <span className="text-xs text-gray-400 font-display">incl. VAT</span>
            </div>
            {product.packsize && product.packsize > 1 && price && (
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                ≈ {formatPrice(parseFloat(price) / product.packsize)} per unit
              </div>
            )}
          </div>

          {/* Details */}
          {(product.volume || product.size) && (
            <div className="flex gap-4 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Size:</span>
              <span className="font-medium text-gray-800 dark:text-white">
                {product.volume || product.size}
              </span>
            </div>
          )}
          {product.stock_code && (
            <div className="flex gap-4 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Code:</span>
              <span className="font-medium text-gray-800 dark:text-white font-mono">
                {product.stock_code}
              </span>
            </div>
          )}

          {/* Quantity & Add */}
          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
              <button
                onClick={() => setQty(q => Math.max(1, q - 1))}
                className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                min={1}
                max={999}
                value={qtyDraft}
                onChange={e => setQtyDraft(e.target.value)}
                onBlur={() => {
                  const n = parseInt(qtyDraft, 10)
                  if (!isNaN(n) && n >= 1) setQty(Math.max(1, Math.min(999, n)))
                  else setQtyDraft(String(qty))
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter')  { e.target.blur() }
                  if (e.key === 'Escape') { setQtyDraft(String(qty)); e.target.blur() }
                }}
                onClick={e => e.target.select()}
                className="w-12 text-center font-display font-semibold text-gray-900 dark:text-white bg-transparent focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700 py-3 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                onClick={() => setQty(q => q + 1)}
                className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>

            <button
              onClick={handleAddToCart}
              disabled={!stockOk || adding}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
            >
              <ShoppingCart size={18} />
              {adding ? 'Adding…' : stockOk ? 'Add to Cart' : 'Out of Stock'}
            </button>

            <button
              onClick={() => toggle(product)}
              className={`p-3 rounded-lg border transition-colors ${
                wished
                  ? 'bg-primary border-primary text-white'
                  : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:border-primary hover:text-primary'
              }`}
            >
              <Heart size={20} fill={wished ? 'currentColor' : 'none'} />
            </button>
          </div>

          {/* Checkout now */}
          {stockOk && (
            <CheckoutNowButton product={product} qty={qty} addToCart={handleAddToCart} adding={adding} />
          )}

          {/* Shipping info */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            {[
              { icon: Truck, text: 'Bulk delivery available' },
              { icon: Package, text: 'Secure packaging' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-300">
                <Icon size={16} className="text-primary flex-shrink-0" />
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-10">
        <div className="flex border-b border-gray-200 dark:border-gray-700 gap-6">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 text-sm font-display font-semibold transition-colors border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'border-primary text-primary dark:text-red-400'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="py-6 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
          {activeTab === 'description' && (
            <p>{product.description || `${name} — Premium quality product distributed by Tony's Liquor. Contact us for bulk pricing and availability.`}</p>
          )}
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ['Brand', product.brand],
                  ['Stock Code', product.stock_code],
                  ['Barcode', product.barcode],
                  ['Volume/Size', product.details?.volume_ml ? `${product.details.volume_ml}ml` : (product.volume || product.size)],
                  ['Alcohol Content', product.details?.alcohol_content],
                  ['Country of Origin', product.details?.country_of_origin],
                  ['Year', product.details?.year],
                  ['Category', product.category_name || product.category],
                  ['Pack Size', product.packsize ? `${product.packsize} units` : null],
                  ['Price (incl. VAT)', formatPrice(price)],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label} className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-gray-500 dark:text-gray-400">{label}</span>
                    <span className="font-medium text-gray-800 dark:text-white text-right max-w-[60%]">{value}</span>
                  </div>
                ))}
              </div>
              {product.details?.tasting_notes && (
                <div>
                  <h4 className="font-display font-semibold text-gray-800 dark:text-white mb-2">Tasting Notes</h4>
                  <p className="text-gray-600 dark:text-gray-300">{product.details.tasting_notes}</p>
                </div>
              )}
              {product.details?.ingredients && (
                <div>
                  <h4 className="font-display font-semibold text-gray-800 dark:text-white mb-2">Ingredients</h4>
                  <p className="text-gray-600 dark:text-gray-300">{product.details.ingredients}</p>
                </div>
              )}
              {product.details?.serving_suggestions && (
                <div>
                  <h4 className="font-display font-semibold text-gray-800 dark:text-white mb-2">Serving Suggestions</h4>
                  <p className="text-gray-600 dark:text-gray-300">{product.details.serving_suggestions}</p>
                </div>
              )}
              {product.details?.storage_info && (
                <div>
                  <h4 className="font-display font-semibold text-gray-800 dark:text-white mb-2">Storage</h4>
                  <p className="text-gray-600 dark:text-gray-300">{product.details.storage_info}</p>
                </div>
              )}
              {product.details?.allergens && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                  <h4 className="font-display font-semibold text-amber-800 dark:text-amber-300 mb-1">Allergens</h4>
                  <p className="text-amber-700 dark:text-amber-400 text-xs">{product.details.allergens}</p>
                </div>
              )}
              {product.details?.warnings && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700">
                  <h4 className="font-display font-semibold text-red-700 dark:text-red-400 mb-1">Warnings</h4>
                  <p className="text-red-600 dark:text-red-300 text-xs">{product.details.warnings}</p>
                </div>
              )}
              {product.details?.features && (
                <div>
                  <h4 className="font-display font-semibold text-gray-800 dark:text-white mb-2">Features</h4>
                  <p className="text-gray-600 dark:text-gray-300">{product.details.features}</p>
                </div>
              )}
            </div>
          )}
          {activeTab === 'promotions' && <PromotionsTab productId={id} />}
        </div>
      </div>

      {/* Related products */}
      {related?.length > 0 && (
        <div className="mt-12">
          <h2 className="section-title mb-6">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(related?.items || related || []).slice(0, 4).map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function CheckoutNowButton({ product, qty, addToCart, adding }) {
  const navigate = useNavigate()
  const addItem = useCartStore(s => s.addItem)

  const handleCheckoutNow = () => {
    addItem({ ...product, quantity: qty })
    navigate('/checkout')
  }

  return (
    <button
      onClick={handleCheckoutNow}
      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-primary text-primary dark:text-primary-light font-display font-semibold text-sm hover:bg-primary hover:text-white transition-all duration-200 mt-1"
    >
      <ShoppingCart size={16} />
      Buy Now — Checkout
    </button>
  )
}

